import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  CostStatus,
  CostSummary,
  CostTrendPoint,
  CostWindow,
} from '@internal/plugin-cost-common';

export interface CostApi {
  status(): Promise<CostStatus>;
  summary(
    window: CostWindow,
    aggregate?: 'namespace' | 'controller',
  ): Promise<CostSummary>;
  trend(window: CostWindow): Promise<CostTrendPoint[]>;
}

export const costApiRef = createApiRef<CostApi>({ id: 'plugin.cost.service' });

export class CostClient implements CostApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  /** Unwraps Backstage's error envelope so the real reason reaches the UI. */
  private async request<T>(path: string): Promise<T> {
    const base = await this.discoveryApi.getBaseUrl('cost');
    const res = await this.fetchApi.fetch(`${base}${path}`);
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) detail = body.error.message;
      } catch {
        // Non-JSON body; the status line is all we have.
      }
      throw new Error(detail);
    }
    return (await res.json()) as T;
  }

  async status() {
    return this.request<CostStatus>('/status');
  }

  async summary(window: CostWindow, aggregate: 'namespace' | 'controller' = 'namespace') {
    return this.request<CostSummary>(
      `/summary?window=${window}&aggregate=${aggregate}`,
    );
  }

  async trend(window: CostWindow) {
    return (
      await this.request<{ points: CostTrendPoint[] }>(`/trend?window=${window}`)
    ).points;
  }
}
